import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Card, Form, Input, message, Row } from 'antd';
import { findIndex, get, isNaN, map } from 'lodash';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../../../../AppContext';
import { ForwardArrow, ProjectIcon } from '../../../../../../assets/svg';
import {
  DEFAULTDATEFORMAT,
  PROPERTY_TYPES,
  ROUTES,
  TAB_KEYS,
} from '../../../../../../common/constants';
import useRouter from '../../../../../../common/useRouter';
import {
  formatNumericInput,
  toCamelCase,
} from '../../../../../../common/utils';
import { CREATE_NEW_SET_PROPERTY } from '../../../../graphql/Mutation';
import { GET_SINGLE_PROJECT } from '../../../../graphql/Queries';
import RenderFields from './RenderFields';

const renderPropertyUi = (
  data,
  isOptionsFocused,
  setIsOptionsFocused,
  isMultiSelectOptionsFocused,
  setIsMultiSelectOptionsFocused,
  isDateFocused,
  setIsDateFocused,
) => {
  return map(data, (item) => {
    switch (item?.type) {
      case PROPERTY_TYPES.NUMERIC:
        return (
          <Form.Item
            label={item?.name}
            name={toCamelCase(item?.name)}
            rules={[
              {
                required: item?.isRequired,
                message: 'This field is required!',
              },
              {
                max: 15,
                message: 'Maximum 15 digits are allowed',
              },
              {
                validator: (_, value) => {
                  if (!value || value === '') {
                    return Promise?.resolve();
                  }
                  const numericValue = Number(value);
                  if (Number?.isNaN(numericValue)) {
                    return Promise?.reject(
                      new Error('Please enter a valid number'),
                    );
                  }
                  return Promise?.resolve();
                },
              },
            ]}
            normalize={(value) => formatNumericInput(value)}
          >
            <Input placeholder="Enter number" inputMode="decimal" allowClear />
          </Form.Item>
        );

      default:
        return (
          <RenderFields
            fieldType={item?.type}
            fieldIndex={toCamelCase(item?.name)}
            fieldName={item?.name}
            fieldIsRequired={item?.isRequired}
            onDateFocused={() => setIsDateFocused(toCamelCase(item?.name))}
            onDateOpenChange={(e) => {
              if (!e) {
                setIsDateFocused();
              }
            }}
            dateOpen={isDateFocused === toCamelCase(item?.name)}
            options={map(item?.options, (option) => ({
              label: option,
              value: option,
            }))}
            onOptionsFocus={() => setIsOptionsFocused(toCamelCase(item?.name))}
            onOptionBlur={() => setIsOptionsFocused(null)}
            onOptionChange={() => setIsOptionsFocused(null)}
            onOptionDropdownVisibilityChange={(e) => {
              if (!e) {
                setIsOptionsFocused();
              } else {
                setIsOptionsFocused(toCamelCase(item?.name));
              }
            }}
            optionsOpen={isOptionsFocused === toCamelCase(item?.name)}
            onMultiSelectFocus={() =>
              setIsMultiSelectOptionsFocused(toCamelCase(item?.name))
            }
            onMultiSelectBlur={() => setIsMultiSelectOptionsFocused(null)}
            multiSelectOpen={
              isMultiSelectOptionsFocused === toCamelCase(item?.name)
            }
          />
        );
    }
  });
};

const AddSetsProperty = () => {
  const { getCurrentUser, dispatch } = useContext(AppContext);
  const currentUserId = getCurrentUser()?.id || {};
  const [form] = Form.useForm();
  const {
    navigate,
    location,
    params: { projectId },
  } = useRouter();
  const [isOptionsFocused, setIsOptionsFocused] = useState(null);
  const [isMultiSelectOptionsFocused, setIsMultiSelectOptionsFocused] =
    useState(null);
  const [isDateFocused, setIsDateFocused] = useState(null);
  const registerDetailsData = location?.state?.registerDetailsData;
  const registerProperty = location?.state?.registerProperty;
  const navFilter = location?.state?.navFilter;
  const navPagination = location?.state?.navPagination;
  const registerId = registerDetailsData?.id;
  const registerName = toCamelCase(
    registerDetailsData?.name?.split(' ')?.join(''),
  );

  const [getWebProject, { data: projectData }] = useLazyQuery(
    GET_SINGLE_PROJECT,
    {
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        dispatch({
          type: 'SET_PROJECT_DETAILS',
          data: JSON.stringify(res?.getProject),
        });
        const index = findIndex(
          res?.getProject?.projectUsers,
          (user) => Number(user?.userId) === Number(currentUserId),
        );
        if (index !== -1) {
          const loggedInUserRole =
            res?.getProject?.projectUsers?.[index]?.roles;
          dispatch({ type: 'SET_PROJECT_ROLE', data: loggedInUserRole });
        } else {
          dispatch({ type: 'SET_PROJECT_ROLE', data: '' });
        }
      },
    },
  );

  const [createNewSetProperty, { loading }] = useMutation(
    CREATE_NEW_SET_PROPERTY,
    {
      fetchPolicy: 'network-only',
      onError() {},
      onCompleted(response) {
        const registerLogId = get(response, 'createRegisterLog.registerLogId');
        navigate(
          `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/${registerLogId}`,
          {
            replace: true,
            state: {
              registerDetailsData,
              registerId,
              registerName,
              registerLogId,
            },
          },
        );
      },
    },
  );

  const onFinish = async () => {
    try {
      const formValues = form.getFieldsValue();

      const result = map(registerProperty, (item) => {
        const key = toCamelCase(item?.name);
        // eslint-disable-next-line no-nested-ternary
        const value = Array.isArray(formValues[key])
          ? formValues[key]?.join(',')
          : item?.type === PROPERTY_TYPES.DATE && formValues[key]
            ? moment(formValues[key]).format(DEFAULTDATEFORMAT)
            : formValues[key];

        if (value === undefined || value === '') return null;

        return {
          registerDetailId: item?.id,
          value: value || '',
        };
      })?.filter(Boolean);
      await createNewSetProperty({
        variables: {
          registerId,
          data: { registerDetailValues: result },
        },
      });
    } catch (error) {
      message.destroy();
      message.error('Something went wrong.');
      return error;
    }
  };

  useEffect(() => {
    if (!isNaN(Number(projectId))) {
      getWebProject({
        variables: {
          id: projectId,
        },
      });
    }
  }, [projectId]);

  const handleCancel = () => {
    form.resetFields();
    navigate(-1);
  };

  return (
    <div className="add-project-wrapper">
      <div className="project-details-title d-flex justify-between page-header">
        <div className="project-details-title-text d-flex align-center">
          <ProjectIcon
            onClick={() => {
              navigate(ROUTES.PROJECTS);
            }}
            className="pointer"
          />
          <ForwardArrow />
          <h1
            className="pointer mr-10 text-primary"
            onClick={() => {
              navigate(`${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}`, {
                state: {
                  navFilter,
                  navPagination,
                },
              });
            }}
          >
            {projectData?.getProject?.name}
          </h1>
          <ForwardArrow />
          <h1 className="mr-10">{registerDetailsData?.name}</h1>
        </div>
      </div>
      <div className="project-module mb-20">
        <Form layout="vertical" form={form} onFinish={onFinish}>
          <Card>
            <h3>Add Details</h3>
            {renderPropertyUi(
              registerProperty,
              isOptionsFocused,
              setIsOptionsFocused,
              isMultiSelectOptionsFocused,
              setIsMultiSelectOptionsFocused,
              isDateFocused,
              setIsDateFocused,
            )}
          </Card>
          <Row justify="end" className="mt-10">
            <div className="form-buttons">
              <Button shape="round" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                shape="round"
                type="primary"
                className="save-button"
                htmlType="submit"
                loading={loading}
              >
                Create
              </Button>
            </div>
          </Row>
        </Form>
      </div>
    </div>
  );
};

export default AddSetsProperty;
